import { Link } from 'react-router';
import { Mail, Send, SquareArrowOutUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardBody, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useInvoiceEmails } from '@/hooks/useInvoices';
import { formatDate } from '@/lib/format';
import { EmailKindBadge } from '@/features/emails/EmailKindBadge';

/**
 * The two send methods are not the same claim, so they do not get the same
 * chip. `billable` is a delivery we performed through Resend — a solid chip.
 * `mail_client` is only a handoff: we composed the message and opened it in
 * the user's own mail app, and whether they hit send is not something we can
 * know. That one gets a dashed outline, which reads as provisional without
 * shouting failure.
 */
const SENT_VIA = {
  billable: { label: 'Sent by Billable', icon: Send },
  mail_client: { label: 'Handed to mail app', icon: SquareArrowOutUpRight }
} as const;

export function InvoiceEmailsCard({ invoiceId }: { invoiceId: string }) {
  const emails = useInvoiceEmails(invoiceId);

  return (
    <Card>
      <CardHeader title="Emails" description="Everything sent about this invoice." />
      <CardBody className="p-0">
        {emails.isPending ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-3.5 w-1/2" />
              </div>
            ))}
          </div>
        ) : !emails.data?.length ? (
          <p className="flex items-center gap-2 px-4 py-5 text-sm text-muted">
            <Mail className="size-4" />
            Nothing sent yet.
          </p>
        ) : (
          <ul className="divide-y divide-[var(--line)]">
            {emails.data.map((email) => {
              const via = SENT_VIA[email.sentVia];
              const ViaIcon = via.icon;
              const handoff = email.sentVia === 'mail_client';
              return (
                <li key={email.id}>
                  <Link
                    to={`/emails/${email.id}`}
                    className="block px-4 py-3 transition-colors hover:bg-[var(--surface-hover)]"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <EmailKindBadge kind={email.kind} />
                      <Badge
                        tone={handoff ? 'outline' : 'neutral'}
                        className={handoff ? 'border-dashed' : undefined}
                      >
                        <ViaIcon className="size-3.5" />
                        {via.label}
                      </Badge>
                    </div>
                    <p className="mt-1 truncate text-sm font-medium">{email.subject}</p>
                    <p className="truncate text-sm text-muted">
                      {email.to.join(', ') || '—'} · {formatDate(email.sentAt.slice(0, 10))}
                    </p>
                    {email.cc.length ? (
                      <p className="truncate text-sm text-subtle">Cc {email.cc.join(', ')}</p>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
